import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Button,
  Badge,
  Card,
  CardContent,
  Input,
  Tooltip,
  Field,
  FieldLabel,
  FieldError,
  type PaginationState,
  PaginatedTable,
  type PaginatedTableColumn,
  Modal,
  Drawer,
} from '../../../../components/ui';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import {
  Court,
  courtFormSchema,
  hasAcademyManagementRole,
  locationFormSchema,
  type ClubOperatingHourSlot,
  type CourtFormData,
  type LocationFormData,
  type Location,
  hasClubManagementRole,
  TierFeatureKey,
  requiresLocationBeforeCourts,
} from '@court-sync/shared';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';
import { useCourts, useLocations } from '../../../../hooks/useDataStore';
import { locationService } from '../../../../services/location';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { AlertTriangle, Copy, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { FeatureGate } from '@/components/FeatureGate';
import { AcademySportMultiSelectField } from '@/components/sports/AcademySportMultiSelectField';
import { useAcademySports } from '@/queries/academySports';
import { resolvePrimaryAcademySportId, getAcademySportLabel } from '@/utils/sportPresentation';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useFacilityClub } from '@/hooks/useFacilityClub';
import { useUserAcademies } from '@/queries/academy';
import { FacilitySettingsPanel } from '@/components/club/FacilitySettingsPanel';
import { LocationHoursOverrideSection } from '@/components/club/LocationHoursOverrideSection';
import ClubCourtManagement from '@/pages/dashboards/club/ClubCourtManagement';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import { ACADEMY_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';

function formatCourtSurfaceLabel(surface: string | null | undefined): string {
  return surface ? surface.charAt(0).toUpperCase() + surface.slice(1) : '—';
}

function formatCourtTypeLabel(courtType: string | null | undefined): string {
  if (!courtType) return '—';
  if (courtType === 'both') return 'Singles and doubles';
  if (courtType === 'singles') return 'Singles only';
  if (courtType === 'doubles') return 'Doubles only';
  return courtType.charAt(0).toUpperCase() + courtType.slice(1);
}

type MainTab = 'courts' | 'locations' | 'settings';

const COURT_FORM_ID = 'court-form';
const LOCATION_FORM_ID = 'location-form';

/**
 * Courts and locations. Facilities are club-owned, so club-only operators (no academy)
 * manage them through the club-scoped page instead of the academy surface below.
 */
export default function CourtManagement({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const { user } = useAuthStore();
  const { academies: userAcademies, isLoading: academiesLoading } = useUserAcademies();
  const { isLoading: clubAccessLoading } = useClubModuleAccess();

  const hasAcademySurface =
    hasAcademyManagementRole(user) || userAcademies.length > 0;

  if (academiesLoading || clubAccessLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (hasAcademySurface) {
    return <AcademyCourtManagement embedded={embedded} />;
  }

  if (hasClubManagementRole(user)) {
    return <ClubCourtManagement embedded={embedded} />;
  }

  return <AcademyCourtManagement embedded={embedded} />;
}

function AcademyCourtManagement({ embedded = false }: OnboardingEmbeddedPageProps = {}) {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const { facilityClub, isLoading: facilityClubLoading } = useFacilityClub(currentAcademy);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasClubManagement } = useClubModuleAccess();
  const showAcademyAccess = hasAcademyManagementRole(user) && hasClubManagement;
  const { courts, isLoading: loading, createCourt, updateCourt, deleteCourt, refresh } = useCourts(currentAcademy?.id);
  const { sports: academySports } = useAcademySports(currentAcademy?.id);
  const primarySportId = resolvePrimaryAcademySportId(academySports);
  const sportLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of academySports) {
      m.set(s.id, getAcademySportLabel(s));
    }
    return m;
  }, [academySports]);
  const [submitting, setSubmitting] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [courtsPage, setCourtsPage] = useState(1);
  const courtsPerPage = 10;

  const hasMultiLocation = useHasMultiLocation();
  const { locations, isLoading: locationsLoading, refresh: refreshLocations } = useLocations(
    currentAcademy?.id,
    { includeInactive: true, enabled: hasMultiLocation }
  );
  const [locationsPage, setLocationsPage] = useState(1);
  const locationsPerPage = 10;
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);

  const [locationFormSubmitting, setLocationFormSubmitting] = useState(false);
  const [locationPendingDelete, setLocationPendingDelete] = useState<Location | null>(null);
  const [locationHoursOverride, setLocationHoursOverride] = useState<ClubOperatingHourSlot[] | null>(null);
  const [mainTab, setMainTab] = useState<MainTab>('courts');

  useEffect(() => {
    if (showForm) {
      setMainTab('courts');
    }
  }, [showForm]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'locations' && hasMultiLocation) {
      setMainTab('locations');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else if (tab === 'settings') {
      setMainTab('settings');
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, hasMultiLocation]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<CourtFormData>({
    resolver: zodResolver(courtFormSchema),
    defaultValues: {
      isActive: true,
      availableForHire: false,
      locationId: '',
      academySportIds: [],
    },
  });

  const watchedSportIds = watch('academySportIds') ?? [];
  const includesTennis = useMemo(
    () =>
      academySports.some(
        (sport) => sport.sportId === 'tennis' && watchedSportIds.includes(sport.id)
      ),
    [academySports, watchedSportIds]
  );

  useEffect(() => {
    if (!includesTennis) {
      setValue('surfaceType', undefined);
    }
  }, [includesTennis, setValue]);

  const {
    register: registerLocation,
    handleSubmit: handleSubmitLocation,
    control: locationControl,
    reset: resetLocation,
    setValue: setLocationField,
    formState: { errors: locationErrors },
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      address: '',
      googleMapsPlaceId: '',
      googleMapsAddress: '',
      isActive: true,
      sortOrder: 0,
    },
  });

  // Check if user has admin access
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const onSubmit = async (data: CourtFormData) => {
    if (!currentAcademy) {
      toast.error('No academy selected');
      return;
    }

    try {
      setSubmitting(true);

      const locId =
        hasMultiLocation && locations.length > 0 && data.locationId && data.locationId.length > 0
          ? data.locationId
          : undefined;

      if (editingCourt) {
        const { locationId: _formLocationId, ...courtFields } = data;
        const payload: Parameters<typeof updateCourt>[1] = {
          ...courtFields,
          surfaceType: includesTennis ? data.surfaceType || undefined : null,
          academySportIds: data.academySportIds?.length ? data.academySportIds : undefined,
          ...(hasClubManagement ? { availableForHire: data.availableForHire ?? false } : {}),
          ...(hasMultiLocation && locations.length > 0
            ? { locationId: locId ?? null }
            : {}),
        };
        await updateCourt(editingCourt.id, payload);
        toast.success('Court updated successfully');
      } else {
        const courtData = {
          academyId: currentAcademy.id,
          name: data.name!,
          surfaceType: includesTennis ? data.surfaceType || undefined : undefined,
          courtType: data.courtType || undefined,
          isActive: data.isActive!,
          ...(hasClubManagement ? { availableForHire: data.availableForHire ?? false } : {}),
          academySportIds:
            data.academySportIds?.length
              ? data.academySportIds
              : primarySportId
                ? [primarySportId]
                : undefined,
          ...(hasMultiLocation && locations.length > 0 && locId ? { locationId: locId } : {}),
        };
        await createCourt(courtData);
        toast.success('Court created successfully');
      }

      // Refresh the store to ensure all components see the updated data
      await refresh();
      handleCancelEdit();
    } catch (error: any) {
      console.error('Error saving court:', error);
      toast.error(error.message || 'Failed to save court');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (court: Court) => {
    setEditingCourt(court);
    setShowForm(true);
    reset({
      name: court.name,
      surfaceType: court.surfaceType,
      courtType: court.courtType,
      isActive: court.isActive,
      availableForHire: court.availableForHire ?? false,
      locationId: court.locationId || '',
      academySportIds: court.academySportIds ?? (primarySportId ? [primarySportId] : []),
    });
  };

  const handleDelete = async (court: Court) => {
    if (!confirm(`Are you sure you want to delete "${court.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCourt(court.id);
      toast.success('Court deleted successfully');
      // Refresh the store to ensure all components see the updated data
      await refresh();
    } catch (error: any) {
      console.error('Error deleting court:', error);
      toast.error(error.message || 'Failed to delete court');
    }
  };

  const handleDuplicate = async (court: Court) => {
    try {
      setSubmitting(true);

      // Create court data with "Duplicate" suffix
      const duplicateData = {
        academyId: court.academyId,
        name: `${court.name} - Duplicate`,
        surfaceType: court.surfaceType,
        courtType: court.courtType,
        isActive: court.isActive,
        ...(hasClubManagement ? { availableForHire: court.availableForHire ?? false } : {}),
        academySportIds: court.academySportIds,
        ...(court.locationId ? { locationId: court.locationId } : {}),
      };

      await createCourt(duplicateData);
      toast.success('Court duplicated successfully');
      // Refresh the store to ensure all components see the updated data
      await refresh();
    } catch (error: any) {
      console.error('Error duplicating court:', error);
      toast.error(error.message || 'Failed to duplicate court');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCourt(null);
    setShowForm(false);
    reset({
      name: '',
      surfaceType: undefined,
      courtType: undefined,
      isActive: true,
      availableForHire: false,
      locationId: '',
      academySportIds: primarySportId ? [primarySportId] : [],
    });
  };

  const handleOpenNewCourt = () => {
    setEditingCourt(null);
    reset({
      name: '',
      surfaceType: undefined,
      courtType: undefined,
      isActive: true,
      availableForHire: false,
      locationId: '',
      academySportIds: primarySportId ? [primarySportId] : [],
    });
    setShowForm(true);
  };

  const sortedCourts = useMemo(
    () =>
      [...courts].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      ),
    [courts]
  );

  const locationById = useMemo(() => {
    const m: Record<string, Location> = {};
    for (const loc of locations) {
      m[loc.id] = loc;
    }
    return m;
  }, [locations]);

  const sortedLocationsForList = useMemo(
    () =>
      locations
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [locations]
  );

  const courtCountByLocationId = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of sortedCourts) {
      if (c.locationId) {
        m[c.locationId] = (m[c.locationId] ?? 0) + 1;
      }
    }
    return m;
  }, [sortedCourts]);

  const closeLocationModal = () => {
    setShowLocationModal(false);
    setEditingLocation(null);
    resetLocation({
      name: '',
      address: '',
      googleMapsPlaceId: '',
      googleMapsAddress: '',
      isActive: true,
      sortOrder: 0,
    });
  };

  const handleOpenLocationModalCreate = () => {
    if (!currentAcademy?.id) return;

    setEditingLocation(null);
    const isFirstSite = sortedLocationsForList.length === 0;

    resetLocation({
      name: isFirstSite ? currentAcademy.name || 'Main location' : '',
      address: isFirstSite ? currentAcademy.address || '' : '',
      googleMapsPlaceId: '',
      googleMapsAddress: isFirstSite
        ? currentAcademy.googleMapsAddress || currentAcademy.address || ''
        : '',
      isActive: true,
      sortOrder: isFirstSite ? 0 : sortedLocationsForList.length,
    });
    setLocationHoursOverride(null);
    setShowLocationModal(true);
  };

  const handleOpenLocationModalEdit = (loc: Location) => {
    setEditingLocation(loc);
    resetLocation({
      name: loc.name,
      address: loc.address ?? '',
      googleMapsPlaceId: loc.googleMapsPlaceId ?? '',
      googleMapsAddress: loc.googleMapsAddress ?? loc.address ?? '',
      isActive: loc.isActive,
      sortOrder: loc.sortOrder,
    });
    setLocationHoursOverride(loc.operatingHoursOverride ?? null);
    setShowLocationModal(true);
  };

  const submitLocationForm = async (data: LocationFormData) => {
    if (!currentAcademy?.id || !hasValidSubscription) {
      toast.error('No academy selected or subscription inactive');
      return;
    }

    try {
      setLocationFormSubmitting(true);
      const trimmed: LocationFormData = {
        ...data,
        name: data.name.trim(),
        address: data.address?.trim() || '',
        googleMapsAddress: data.googleMapsAddress?.trim() || '',
        googleMapsPlaceId: data.googleMapsPlaceId?.trim() || '',
        isActive: data.isActive ?? true,
        sortOrder: data.sortOrder ?? 0,
        operatingHoursOverride: locationHoursOverride,
      };

      if (editingLocation) {
        await locationService.updateLocation(editingLocation.id, {
          academyId: currentAcademy.id,
          ...trimmed,
        });
        toast.success('Location updated');
      } else {
        await locationService.createLocation({
          academyId: currentAcademy.id,
          ...trimmed,
        });
        toast.success('Location created');
      }

      closeLocationModal();
      await refreshLocations();
      await refresh();
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to save location');
    } finally {
      setLocationFormSubmitting(false);
    }
  };

  const cancelLocationDelete = () => setLocationPendingDelete(null);

  const requestDeleteLocation = (loc: Location) => setLocationPendingDelete(loc);

  const confirmDeleteLocation = async () => {
    const loc = locationPendingDelete;
    if (!loc || !currentAcademy?.id || !hasValidSubscription) return;

    try {
      setLocationFormSubmitting(true);
      await locationService.deleteLocation(loc.id, currentAcademy.id);
      toast.success('Location deleted');
      setLocationPendingDelete(null);
      await refreshLocations();
      await refresh();
      if (editingLocation?.id === loc.id) {
        closeLocationModal();
      }
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to delete location');
    } finally {
      setLocationFormSubmitting(false);
    }
  };

  const courtTableColumns: PaginatedTableColumn<Court>[] = [
    {
      key: 'name',
      header: 'Court',
      render: (court) => <span className="font-medium text-gray-900 dark:text-white">{court.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (court) =>
        court.isActive ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (court) => {
        let label: string;
        if (!court.locationId) {
          label = hasMultiLocation && locations.length > 0 ? 'Default (academy address)' : '—';
        } else {
          const loc = court.locationId ? locationById[court.locationId] : undefined;
          label = loc?.name ?? 'Unknown location';
        }
        return <span className="text-muted-foreground">{label}</span>;
      },
    },
    {
      key: 'sports',
      header: 'Sports',
      render: (court) => {
        const ids = court.academySportIds ?? [];
        if (ids.length === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-muted-foreground text-sm">
            {ids.map((id) => sportLabelById.get(id) ?? 'Sport').join(', ')}
          </span>
        );
      },
    },
    {
      key: 'surface',
      header: 'Surface',
      render: (court) => formatCourtSurfaceLabel(court.surfaceType),
    },
    {
      key: 'courtType',
      header: 'Type',
      render: (court) => formatCourtTypeLabel(court.courtType),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right w-[1%] whitespace-nowrap',
      render: (court) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => handleEdit(court)}
              disabled={!hasValidSubscription}
              aria-label="Edit court"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => handleDuplicate(court)}
              disabled={submitting || !hasValidSubscription}
              className="text-blue-600 hover:text-blue-700 hover:border-blue-300"
              aria-label="Duplicate court"
            >
              <Copy className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => handleDelete(court)}
              disabled={!hasValidSubscription}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
              aria-label="Delete court"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const locationTableColumns: PaginatedTableColumn<Location>[] = [
    {
      key: 'name',
      header: 'Location',
      render: (loc) => (
        <Link
          to={`/admin/courts/location/${loc.id}`}
          className="font-medium text-tennis-700 hover:underline dark:text-tennis-400"
          onClick={(e) => e.stopPropagation()}
        >
          {loc.name}
        </Link>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      render: (loc) => (
        <span className="text-muted-foreground line-clamp-2 max-w-[20rem]">
          {loc.address || loc.googleMapsAddress || '—'}
        </span>
      ),
    },
    {
      key: 'courts',
      header: 'Courts',
      className: 'whitespace-nowrap',
      render: (loc) => {
        const n = courtCountByLocationId[loc.id] ?? 0;
        return <span>{n === 0 ? 'No courts yet' : `${n} court${n === 1 ? '' : 's'}`}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (loc) =>
        loc.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Inactive</Badge>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right w-[1%] whitespace-nowrap',
      render: (loc) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => handleOpenLocationModalEdit(loc)}
              disabled={!hasValidSubscription}
              aria-label="Edit location"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => requestDeleteLocation(loc)}
              disabled={!hasValidSubscription || locationFormSubmitting}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
              aria-label="Delete location"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];
  const courtsTotalPages = Math.max(1, Math.ceil(sortedCourts.length / courtsPerPage));
  const paginatedCourts = sortedCourts.slice(
    (courtsPage - 1) * courtsPerPage,
    courtsPage * courtsPerPage
  );

  const locationsTotalPages = Math.max(1, Math.ceil(sortedLocationsForList.length / locationsPerPage));
  const paginatedLocations = sortedLocationsForList.slice(
    (locationsPage - 1) * locationsPerPage,
    locationsPage * locationsPerPage
  );

  useEffect(() => {
    setCourtsPage(1);
    setLocationsPage(1);
  }, [currentAcademy?.id]);

  useEffect(() => {
    if (courtsPage > courtsTotalPages) {
      setCourtsPage(courtsTotalPages);
    }
  }, [courtsPage, courtsTotalPages]);

  useEffect(() => {
    if (locationsPage > locationsTotalPages) {
      setLocationsPage(locationsTotalPages);
    }
  }, [locationsPage, locationsTotalPages]);

  if (!user || !hasAcademyManagementRole(user)) {
    return null;
  }

  // Academy data may still be loading from preload
  if (!currentAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const pendingDeleteCourtCount =
    locationPendingDelete != null ? (courtCountByLocationId[locationPendingDelete.id] ?? 0) : 0;

  // Multi-location is optional: courts created on Starter (no locations) must remain visible after upgrade.
  const requiresLocationBeforeCourtsGate = requiresLocationBeforeCourts({
    hasMultiLocation,
    locationsLoading,
    locationCount: locations.length,
    courtCount: sortedCourts.length,
  });

  const showCourtsHeaderAction =
    mainTab === 'courts' &&
    !loading &&
    sortedCourts.length > 0 &&
    !(hasMultiLocation && locationsLoading);

  const showLocationsHeaderAction =
    mainTab === 'locations' && hasMultiLocation && !locationsLoading && locations.length > 0;

  const courtsHelpText =
    mainTab === 'locations'
      ? 'Manage physical sites and court groupings'
      : hasMultiLocation && locations.length > 0
        ? 'Courts without a site and quick access across locations'
        : 'Manage your courts and facilities';

  return (
    <div className={embedded ? 'min-h-0' : 'min-h-screen'}>
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Courts"
            help={courtsHelpText}
            helpAnchorId={ACADEMY_PAGE_TOUR_STEP_IDS.COURTS_TABS}
            actions={
              showCourtsHeaderAction ? (
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full md:w-auto"
                    onClick={handleOpenNewCourt}
                    disabled={!hasValidSubscription}
                  >
                    <Plus aria-hidden />
                    New Court
                  </Button>
                </Tooltip>
              ) : showLocationsHeaderAction ? (
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    id={ACADEMY_PAGE_TOUR_STEP_IDS.LOCATIONS_NEW}
                    type="button"
                    size="sm"
                    className="w-full md:w-auto"
                    onClick={handleOpenLocationModalCreate}
                    disabled={!hasValidSubscription}
                  >
                    <Plus aria-hidden />
                    Add Location
                  </Button>
                </Tooltip>
              ) : undefined
            }
          />
        ) : null}

        <div id={ACADEMY_PAGE_TOUR_STEP_IDS.COURTS_TABS}>
        <DashboardTabNav<MainTab>
          role="tablist"
          tabs={[
            { id: 'courts' as const, label: 'Courts', tabId: 'courts-main-tab' },
            ...(hasMultiLocation
              ? [{ id: 'locations' as const, label: 'Locations', tabId: 'locations-main-tab' }]
              : []),
            { id: 'settings' as const, label: 'Settings', tabId: 'facility-settings-tab' },
          ]}
          activeTab={mainTab}
          onChange={setMainTab}
          ariaLabel="Courts and locations"
        />
        </div>

        <Drawer
          isOpen={showLocationModal && hasMultiLocation}
          onClose={closeLocationModal}
          title={editingLocation ? 'Edit location' : 'Add location'}
          width="2xl"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeLocationModal}>
                Cancel
              </Button>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  type="submit"
                  form={LOCATION_FORM_ID}
                  isLoading={locationFormSubmitting}
                  disabled={!hasValidSubscription}
                >
                  {editingLocation ? 'Save location' : 'Create location'}
                </Button>
              </Tooltip>
            </div>
          }
        >
          <form
            id={LOCATION_FORM_ID}
            onSubmit={handleSubmitLocation(submitLocationForm)}
            className="space-y-4"
          >
            <Field data-invalid={!!locationErrors.name?.message}>
              <FieldLabel htmlFor="loc-name">Location name *</FieldLabel>
              <Input
                id="loc-name"
                aria-invalid={!!locationErrors.name?.message}
                {...registerLocation('name')}
                placeholder="Main location"
              />
              <FieldError>{locationErrors.name?.message}</FieldError>
            </Field>

            <Controller
              name="address"
              control={locationControl}
              render={({ field }) => (
                <AddressAutocomplete
                  label="Address"
                  value={field.value ?? ''}
                  onChange={(v) => {
                    field.onChange(v);
                    setLocationField('googleMapsPlaceId', '');
                    setLocationField('googleMapsAddress', '');
                  }}
                  onPlaceResolved={(p) => {
                    field.onChange(p.formattedAddress);
                    setLocationField('googleMapsAddress', p.formattedAddress);
                    setLocationField('googleMapsPlaceId', p.placeId ?? '');
                  }}
                  placeholder="Start typing to search with Google"
                />
              )}
            />

            <div className="flex items-center gap-2">
              <input
                id="loc-active"
                type="checkbox"
                {...registerLocation('isActive')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="loc-active" className="text-sm text-gray-700 dark:text-gray-300">
                Active (visible on your public site where applicable)
              </label>
            </div>

            <input
              type="hidden"
              {...registerLocation('sortOrder', {
                valueAsNumber: true,
              })}
            />

            <LocationHoursOverrideSection
              value={locationHoursOverride}
              onChange={setLocationHoursOverride}
              disabled={locationFormSubmitting}
            />
          </form>
        </Drawer>

        <Modal
          isOpen={!!locationPendingDelete && hasMultiLocation}
          onClose={cancelLocationDelete}
          title="Remove this location?"
          maxWidth="lg"
        >
          {locationPendingDelete && (
            <div className="space-y-4">
              <div
                role="alert"
                className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <div className="space-y-2 min-w-0">
                  <p className="font-semibold">This action is permanent and cannot be undone.</p>
                  {pendingDeleteCourtCount > 0 ? (
                    <p>
                      Removing &quot;{locationPendingDelete.name}&quot; deletes this location and permanently removes{' '}
                      <strong>
                        {pendingDeleteCourtCount} court{pendingDeleteCourtCount === 1 ? '' : 's'}
                      </strong>{' '}
                      assigned to this site. Bookings and schedules that reference those courts may be affected.
                    </p>
                  ) : (
                    <p>This will remove the venue &quot;{locationPendingDelete.name}&quot; from your academy.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={cancelLocationDelete}>
                  Cancel
                </Button>
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={confirmDeleteLocation}
                    isLoading={locationFormSubmitting}
                    disabled={!hasValidSubscription}
                  >
                    Remove location
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}
        </Modal>

        <Drawer
          isOpen={showForm}
          onClose={handleCancelEdit}
          title={editingCourt ? 'Edit Court' : 'Create New Court'}
          width="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  type="submit"
                  form={COURT_FORM_ID}
                  isLoading={submitting}
                  disabled={!hasValidSubscription}
                >
                  {editingCourt ? 'Update Court' : 'Create Court'}
                </Button>
              </Tooltip>
            </div>
          }
        >
          <form id={COURT_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Field data-invalid={!!errors.name?.message}>
              <FieldLabel htmlFor="court-name">Court Name *</FieldLabel>
              <Input
                id="court-name"
                aria-invalid={!!errors.name?.message}
                {...register('name')}
                placeholder="Court 1"
              />
              <FieldError>{errors.name?.message}</FieldError>
            </Field>

            <AcademySportMultiSelectField
              academyId={currentAcademy.id}
              control={control}
              name="academySportIds"
              disabled={!hasValidSubscription || submitting}
            />

            {includesTennis ? (
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  htmlFor="court-surface"
                >
                  Surface Type
                </label>
                <select id="court-surface" {...register('surfaceType')} className="input">
                  <option value="">Select surface type</option>
                  <option value="hard">Hard Court</option>
                  <option value="clay">Clay Court</option>
                  <option value="grass">Grass Court</option>
                  <option value="indoor">Indoor Court</option>
                </select>
                {errors.surfaceType && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.surfaceType.message}
                  </p>
                )}
              </div>
            ) : null}

            <div>
              <label
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                htmlFor="court-type-select"
              >
                Court Type
              </label>
              <select id="court-type-select" {...register('courtType')} className="input">
                <option value="">Select court type</option>
                <option value="singles">Singles Only</option>
                <option value="doubles">Doubles Only</option>
                <option value="both">Singles & Doubles</option>
              </select>
              {errors.courtType && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  {errors.courtType.message}
                </p>
              )}
            </div>

            {hasMultiLocation && locations.length > 0 && (
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  htmlFor="court-location"
                >
                  Location
                </label>
                <select id="court-location" {...register('locationId')} className="input">
                  <option value="">Default (academy address)</option>
                  {locations
                    .filter((l) => l.isActive)
                    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                    .map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                </select>
                {errors.locationId && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.locationId.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  Default uses the academy address when no site is selected.
                </p>
              </div>
            )}

            <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('isActive')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Available for lesson scheduling
              </span>
            </label>

            {hasClubManagement ? (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('availableForHire')}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Available for hire
                </span>
              </label>
            ) : null}
            </div>
          </form>
        </Drawer>

        {!showForm && mainTab === 'locations' && (
          <FeatureGate
            feature={TierFeatureKey.MULTI_LOCATION}
            description="Add multiple venues with separate court lists and addresses. Available on Professional and Enterprise."
          >
            {locationsLoading ? (
              <Card>
                <CardContent>
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                </CardContent>
              </Card>
            ) : hasMultiLocation && !locationsLoading && locations.length === 0 ? (
              <DashboardEmptyStateCard>
                <DashboardEmptyStateIcon icon={MapPin} />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No locations yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create locations to group courts by venue. Addresses can be synced from Google.
                </p>
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    id={ACADEMY_PAGE_TOUR_STEP_IDS.LOCATIONS_NEW}
                    onClick={handleOpenLocationModalCreate}
                    disabled={!hasValidSubscription}
                  >
                    Add Location
                  </Button>
                </Tooltip>
              </DashboardEmptyStateCard>
            ) : hasMultiLocation && locations.length > 0 ? (
              <div id={ACADEMY_PAGE_TOUR_STEP_IDS.LOCATIONS_LIST}>
              <DashboardDataCard>
                  <PaginatedTable<Location>
                    columns={locationTableColumns}
                    data={paginatedLocations}
                    pagination={
                      {
                        page: locationsPage,
                        limit: locationsPerPage,
                        total: sortedLocationsForList.length,
                        totalPages: locationsTotalPages,
                      } satisfies PaginationState
                    }
                    loading={false}
                    emptyMessage="No locations yet."
                    onPageChange={setLocationsPage}
                    onRowClick={handleOpenLocationModalEdit}
                    noWrapperPadding
                  />
              </DashboardDataCard>
              </div>
            ) : null}
          </FeatureGate>
        )}

        {mainTab === 'settings' && facilityClubLoading ? (
          <Card>
            <CardContent>
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            </CardContent>
          </Card>
        ) : mainTab === 'settings' && facilityClub ? (
          <FacilitySettingsPanel
            club={facilityClub}
            showAcademyAccess={showAcademyAccess}
            hasCourtHire={hasClubManagement}
          />
        ) : mainTab === 'settings' ? (
          <Card>
            <CardContent className="py-10 px-6 text-center max-w-lg mx-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Facility settings are unavailable because no club is linked to this academy.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {mainTab === 'courts' && (
          <>
            {loading ? (
              <Card>
                <CardContent>
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                </CardContent>
              </Card>
            ) : hasMultiLocation && locationsLoading ? (
              <Card>
                <CardContent>
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                </CardContent>
              </Card>
            ) : requiresLocationBeforeCourtsGate ? (
              <Card>
                <CardContent className="py-10 px-6 text-center max-w-lg mx-auto">
                  <MapPin className="h-10 w-10 mx-auto text-muted-foreground mb-3" aria-hidden />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Add a location first</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Your plan supports multiple sites. Open the Locations tab to create your first venue, then manage
                    courts here or from each location page.
                  </p>
                  <Button type="button" variant="default" onClick={() => setMainTab('locations')}>
                    Go to Locations
                  </Button>
                </CardContent>
              </Card>
            ) : sortedCourts.length === 0 ? (
              <DashboardEmptyStateCard>
                <DashboardEmptyStateIcon icon={MapPin} />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No courts yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create your first court to manage scheduling and bookings.
                  {hasMultiLocation && locations.length > 0
                    ? ' You can assign courts to locations from the form.'
                    : ''}
                </p>
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button onClick={handleOpenNewCourt} disabled={!hasValidSubscription}>
                    Create your first court
                  </Button>
                </Tooltip>
              </DashboardEmptyStateCard>
            ) : (
              <DashboardDataCard>
                  <PaginatedTable<Court>
                    columns={courtTableColumns}
                    data={paginatedCourts}
                    pagination={
                      {
                        page: courtsPage,
                        limit: courtsPerPage,
                        total: sortedCourts.length,
                        totalPages: courtsTotalPages,
                      } satisfies PaginationState
                    }
                    emptyMessage="No courts found."
                    onPageChange={setCourtsPage}
                    onRowClick={handleEdit}
                    noWrapperPadding
                  />
              </DashboardDataCard>
            )}
          </>
        )}
      </div>
    </div>
  );
}
